/**
 * ActionMenu — menu suspenso de ações (o "⋯" / engrenagem das telas).
 *
 * Substitui a fileira de botões soltos por UM gatilho com as ações dentro.
 * Renderiza com `position: fixed` calculada a partir do gatilho: assim o menu
 * nunca é cortado por um container com overflow (o board do Kanban rola em X)
 * nem fica embaixo de um irmão com z-index (era o bug do popover de filtro
 * coberto pelas abas de funil).
 *
 * Itens: { key, label, icon, onClick, danger, disabled, hint, active }.
 * Um item { section: 'Título' } vira cabeçalho de seção; { divider: true }
 * vira separador.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_W = 240;

const ActionMenu = ({
  items = [],
  colors,
  align = 'right',
  width = MENU_W,
  title,
  // Gatilho: função que recebe { open, toggle } e devolve o elemento; ou um
  // elemento pronto (aí o menu envolve num span clicável).
  trigger,
  triggerStyle,
  stopPropagation = true,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuH = Math.min(items.length * 40 + 24, vh - 24);
    let left = align === 'right' ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, vw - width - 8));
    // Abre para baixo; se não couber, abre para cima.
    let top = r.bottom + 6;
    if (top + menuH > vh - 8) top = Math.max(8, r.top - 6 - menuH);
    setPos({ top, left });
  }, [open, align, width, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const toggle = (e) => {
    if (stopPropagation && e) { e.stopPropagation(); }
    setOpen((v) => !v);
  };

  const triggerEl = typeof trigger === 'function' ? trigger({ open, toggle }) : trigger;

  return (
    <>
      <span
        ref={btnRef}
        onClick={toggle}
        onPointerDown={(e) => stopPropagation && e.stopPropagation()}
        style={{ display: 'inline-flex', ...triggerStyle }}
      >
        {triggerEl}
      </span>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width,
            zIndex: 1200, padding: 6, borderRadius: 12,
            backgroundColor: colors.bgPrimary,
            border: `1px solid ${colors.borderColor}`,
            boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
            maxHeight: 'calc(100vh - 16px)', overflowY: 'auto',
          }}
        >
          {title && (
            <div style={{
              padding: '6px 10px 8px', fontSize: 11, fontWeight: 700,
              letterSpacing: 0.3, textTransform: 'uppercase', color: colors.textTertiary,
            }}>
              {title}
            </div>
          )}
          {items.map((it, i) => {
            if (it.divider) {
              return <div key={`d${i}`} style={{ height: 1, margin: '6px 4px', backgroundColor: colors.borderColor }} />;
            }
            if (it.section) {
              return (
                <div key={`s${i}`} style={{
                  padding: '8px 10px 4px', fontSize: 11, fontWeight: 700,
                  letterSpacing: 0.3, textTransform: 'uppercase', color: colors.textTertiary,
                }}>
                  {it.section}
                </div>
              );
            }
            const Icon = it.icon;
            const color = it.danger ? '#ef4444' : (it.active ? colors.accentPrimary : colors.textPrimary);
            return (
              <button
                key={it.key || i}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                title={it.disabled && it.hint ? it.hint : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (it.disabled) return;
                  setOpen(false);
                  it.onClick?.();
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '8px 10px', borderRadius: 8, border: 'none',
                  background: 'transparent', textAlign: 'left',
                  cursor: it.disabled ? 'not-allowed' : 'pointer',
                  opacity: it.disabled ? 0.45 : 1,
                  color, fontSize: 13, fontWeight: 500,
                }}
                onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = colors.bgSecondary; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {Icon && <Icon size={15} style={{ flexShrink: 0, color: it.danger ? '#ef4444' : (it.active ? colors.accentPrimary : colors.textSecondary) }} />}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.label}
                  </span>
                  {it.hint && !it.disabled && (
                    <span style={{ display: 'block', fontSize: 11, color: colors.textTertiary, marginTop: 1 }}>
                      {it.hint}
                    </span>
                  )}
                </span>
                {it.active && (
                  <span style={{
                    width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                    backgroundColor: '#22c55e',
                  }} />
                )}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

/** Botão-ícone padrão para usar como gatilho do ActionMenu. */
export const IconButton = ({ icon: Icon, label, colors, size = 32, iconSize = 16, active, style, ...rest }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    style={{
      width: size, height: size, borderRadius: 8, border: 'none',
      background: active ? `${colors.accentPrimary}15` : 'transparent',
      color: active ? colors.accentPrimary : colors.textSecondary,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', flexShrink: 0, padding: 0,
      transition: 'background-color 120ms, color 120ms',
      ...style,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgSecondary; e.currentTarget.style.color = colors.textPrimary; }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = active ? `${colors.accentPrimary}15` : 'transparent';
      e.currentTarget.style.color = active ? colors.accentPrimary : colors.textSecondary;
    }}
    {...rest}
  >
    <Icon size={iconSize} />
  </button>
);

export default ActionMenu;
